import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

export default function LoginModal({ show, onHide, reason }) {
  const { loginWithGoogle, loginWithEmail, signupWithEmail } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => { setError(''); setInfo(''); setEmail(''); setPassword(''); };

  const handleGoogle = async () => {
    try {
      setLoading(true);
      setError('');
      await loginWithGoogle();
      onHide();
    } catch (e) {
      setError('Google 로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await loginWithEmail(email, password);
        onHide();
      } else {
        await signupWithEmail(email, password);
        setInfo('인증 메일을 발송했습니다. 이메일을 확인해주세요.');
        setMode('login');
        setPassword('');
      }
    } catch (e) {
      const messages = {
        'auth/user-not-found': '존재하지 않는 계정입니다.',
        'auth/wrong-password': '비밀번호가 올바르지 않습니다.',
        'auth/email-already-in-use': '이미 사용 중인 이메일입니다.',
        'auth/weak-password': '비밀번호는 6자 이상이어야 합니다.',
        'auth/invalid-email': '올바른 이메일 형식이 아닙니다.',
      };
      setError(messages[e.code] || '오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="sm" onExited={reset}>
      <Modal.Body style={{ padding: '32px 28px' }}>
        <h5 style={{ fontWeight: 800, marginBottom: 4 }}>
          {mode === 'login' ? '로그인' : '회원가입'}
        </h5>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>
          {reason === 'share'
            ? '로그인 후 자동으로 공유 링크가 복사됩니다.'
            : reason === 'neb'
            ? '로그인 후 넵 심사가 시작돼요 ✦'
            : '로그인하면 연락처 정보를 확인할 수 있습니다.'}
        </p>

        {error && <Alert variant="danger" style={{ fontSize: 13, padding: '8px 12px' }}>{error}</Alert>}
        {info  && <Alert variant="success" style={{ fontSize: 13, padding: '8px 12px' }}>{info}</Alert>}

        {/* Google 로그인 */}
        <Button
          variant="outline-dark"
          className="w-100 mb-3"
          style={{ fontWeight: 600, fontSize: 14 }}
          onClick={handleGoogle}
          disabled={loading}
        >
          <svg style={{ marginRight: 8, verticalAlign: 'middle' }} width="18" height="18" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.5 19 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5.1l-6.2-5.2C29.4 35.5 26.8 36 24 36c-5.3 0-9.6-3.3-11.2-8H6.4C9.8 35.8 16.3 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.7 2-2 3.8-3.7 5.1l6.2 5.2C37.2 39.4 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
          </svg>
          Google로 계속하기
        </Button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <hr style={{ flex: 1, margin: 0 }} />
          <span style={{ fontSize: 12, color: '#aaa' }}>또는</span>
          <hr style={{ flex: 1, margin: 0 }} />
        </div>

        {/* 이메일 로그인 */}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Control
              type="email"
              placeholder="이메일"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{ fontSize: 14 }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              type="password"
              placeholder="비밀번호"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{ fontSize: 14 }}
            />
          </Form.Group>
          <Button
            type="submit"
            variant="dark"
            className="w-100"
            style={{ fontWeight: 600, fontSize: 14 }}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : mode === 'login' ? '로그인' : '회원가입'}
          </Button>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#888' }}>
          {mode === 'login' ? (
            <>계정이 없으신가요?{' '}
              <span style={{ color: '#1a1a1a', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => { setMode('signup'); reset(); }}>
                회원가입
              </span>
            </>
          ) : (
            <>이미 계정이 있으신가요?{' '}
              <span style={{ color: '#1a1a1a', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => { setMode('login'); reset(); }}>
                로그인
              </span>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
